import { useEffect, useRef, useState } from 'react'

import { Link as RouterLink, Redirect, useHistory } from 'react-router-dom'

import {
  getExtraProperty,
  hasScopesPermission,
} from '@tribeplatform/gql-client'
import { Form, FormRef } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  SystemSpaceSlug,
  useAuthToken,
  useSignup,
  useSpaceUtil,
  useValidateLink,
  useValidateToken,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useRefreshToken } from '../../hooks/useRefreshToken'
import { isMemberNotConfirmed } from '../../lib/app.lib'
import { useQuery } from '../../lib/useQuery'
import { EMAIL_PATTERN } from '../../lib/validator.utils'
import { Ssos } from './Ssos'
import { onAuthenticate } from './utils/onAuthenticate'

export const SignUp = ({ invitationLinkId }) => {
  const { $t } = useI18n()
  const [disabledEmail, setDisabledEmail] = useState(false)
  const formRef = useRef<FormRef>()
  const history = useHistory()
  const { data: authToken } = useAuthToken()
  const { network } = authToken
  const { update } = useRefreshToken()
  const { redirect } = useRouter()

  // get the invitation token if there is any
  const query = useQuery()
  const invitationToken = query.token
  const queryString: { redirect?: string; method?: string } = {}
  if (query.redirect) {
    queryString.redirect = query.redirect
  }
  if (query.method) {
    queryString.method = query.method
  }

  const termsOfServiceUrl =
    getExtraProperty(network?.extraProperties, 'termsOfServiceUrl') ??
    network?.termsOfServiceUrl

  const privacyPolicyUrl =
    getExtraProperty(network?.extraProperties, 'privacyPolicyUrl') ??
    network?.privacyPolicyUrl

  const { generatePath } = useSpaceUtil()

  // validate the invitation link if there is any
  const { error: linkValidationError } = useValidateLink({
    variables: { id: invitationLinkId },
    useQueryOptions: { enabled: !!invitationLinkId, retry: false },
  })

  // validate the token if there is any
  const { data: tokenStatus, error: tokenValidationError } = useValidateToken({
    variables: { token: invitationToken },
    useQueryOptions: { enabled: !!invitationToken, retry: false },
  })

  useEffect(() => {
    if (formRef.current?.methods?.setValue && tokenStatus?.inviteeEmail) {
      formRef.current.methods.setValue('name', tokenStatus.inviteeName)
      formRef.current.methods.setValue('email', tokenStatus.inviteeEmail)
      setDisabledEmail(true)
    }
  }, [formRef, tokenStatus])

  const { mutateAsync: signup } = useSignup({
    invitationLinkId,
    invitationToken,
  })
  const [isLoading, setIsLoading] = useState(false)

  const [
    canLoginNetwork,
    canJoinNetwork,
    canJoinNetworkWithToken,
    canJoinNetworkWithLink,
  ] = hasScopesPermission(network, [
    'loginNetwork',
    'joinNetwork',
    'joinNetworkWithToken',
    'joinNetworkWithInvitationLink',
  ])

  const onSubmit = async data => {
    setIsLoading(true)
    const dataClone = { ...data }
    delete dataClone.checkbox
    await signup(
      {
        input: dataClone,
      },
      {
        onError: () => {
          setIsLoading(false)
        },
        onSuccess: async authToken => {
          const newAuthToken = await update(authToken)
          const { refreshToken, accessToken, member, role } = newAuthToken || {}
          if (accessToken || refreshToken) {
            if (!isMemberNotConfirmed(member, role)) {
              onAuthenticate()
              if (accessToken) {
                redirect('/')
              }
            } else {
              history.replace(
                generatePath(SystemSpaceSlug.VERIFY_EMAIL, null, {
                  memberId: member?.id,
                }),
              )
            }
          }
        },
      },
    )
  }

  // in case of invitation link corruption redirect to generic sign up page
  if (linkValidationError && invitationLinkId) {
    toast({
      description: $t({
        defaultMessage: 'The invitation link has been expired.',
        id: 'Auth.SignUp.LinkExpired',
      }),
      status: 'error',
    })
    return <Redirect to="/auth/signup" />
  }

  // if token is invalid redirect to generic sign up page
  if (tokenValidationError && invitationToken) {
    toast({
      description: $t({
        defaultMessage: 'The invitation link has been expired.',
        id: 'Auth.SignUp.LinkExpired',
      }),
      status: 'error',
    })
    return <Redirect to="/auth/signup" />
  }

  if (!canJoinNetwork && !invitationLinkId && !invitationToken) {
    return <Redirect to="/auth/login" />
  }

  if (!canJoinNetwork && !canJoinNetworkWithLink && !canJoinNetworkWithToken) {
    return <Redirect to="/auth/login" />
  }

  const showSignUpForm = !network.hideDefaultAuthenticationForm

  return (
    <>
      <div className="mx-auto w-full max-w-md text-center text-content-on-background">
        <h2 className="mt-6 text-3xl font-extrabold">
          <T defaultMessage="Create your account" id="Auth.SignUp.Title" />
        </h2>
        {canLoginNetwork && (
          <div className="mt-3 text-lg">
            <T
              defaultMessage="Already a member?"
              id="Auth.SignUp.LoginInstruct"
            />{' '}
            <Link
              as={RouterLink}
              to={generatePath(SystemSpaceSlug.LOGIN, null, queryString)}
            >
              <T
                defaultMessage="Log in now"
                id="Auth.SignUp.LoginInstruct.Link"
              />
            </Link>
          </div>
        )}
      </div>

      <div className="mt-8 mx-auto w-full max-w-md flex flex-col space-y-5">
        <Card className="py-8 px-4">
          {showSignUpForm && (
            <Form
              className="space-y-6"
              onSubmit={onSubmit}
              defaultValues={{
                name: tokenStatus?.inviteeName || '',
                email: tokenStatus?.inviteeEmail || '',
              }}
              ref={formRef}
            >
              <Form.Input
                autoFocus
                name="name"
                autoComplete="name"
                label={
                  <T defaultMessage="Name" id="Auth.SignUp.Input.Name.Label" />
                }
              />
              <Form.Input
                disabled={disabledEmail}
                validation={{
                  required: $t({
                    defaultMessage: 'Email is required',
                    id: 'Auth.SignUp.Form.Validation.MissingEmail',
                  }),
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: $t({
                      defaultMessage: 'Invalid email address',
                      id: 'Generics.Validation.InvalidEmail',
                    }),
                  },
                }}
                name="email"
                autoComplete="email"
                label={<T defaultMessage="Email" id="Generics.Email" />}
              />

              <Form.Input
                validation={{
                  required: $t({
                    defaultMessage: 'Password is required',
                    id: 'Auth.SignUp.Form.Validation.MissingPassword',
                  }),
                }}
                type="password"
                label={
                  <T
                    defaultMessage="Create a password"
                    id="Auth.SignUp.Input.Password.Label"
                  />
                }
                name="password"
                autoComplete="current-password"
              />

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Form.Checkbox
                    name="checkbox"
                    label={
                      <div>
                        <T
                          defaultMessage="By creating an account, I agree to the <tos>Terms of Service</tos> and <privacy>Privacy Policy</privacy>"
                          id="Auth.SignUp.Input.AgreementNotice.Label"
                          values={{
                            tos: chunks =>
                              termsOfServiceUrl ? (
                                <Link
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href={termsOfServiceUrl}
                                >
                                  {chunks}
                                </Link>
                              ) : (
                                <>{chunks}</>
                              ),
                            privacy: chunks =>
                              privacyPolicyUrl ? (
                                <Link
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href={privacyPolicyUrl}
                                >
                                  {chunks}
                                </Link>
                              ) : (
                                <>{chunks}</>
                              ),
                          }}
                        />
                      </div>
                    }
                    validation={{
                      required: $t({
                        defaultMessage:
                          'You must agree with the terms and conditions',
                        id: 'Auth.SignUp.Form.Validation.AgreementRequired',
                      }),
                    }}
                  />
                </div>
              </div>
              <div>
                <Button fullWidth type="submit" loading={isLoading} size="lg">
                  <T
                    defaultMessage="Create my account"
                    id="Auth.SignUp.Actions.Submit"
                  />
                </Button>
              </div>
            </Form>
          )}
          <Ssos
            hasDivider={showSignUpForm}
            invitationLinkId={invitationLinkId}
            invitationToken={invitationToken}
          />
        </Card>
      </div>
    </>
  )
}
